import { cn } from "@rosette/ts/src/util/cn.js";
import { PlayIcon } from "@rosette/web_components/src/icon/PlayIcon.js";
import { PauseIcon } from "@rosette/web_components/src/icon/PauseIcon.js";
import { AudioSettings } from "@rosette/web_components/src/assets/AudioSettings.js";
import { StopIcon } from "@rosette/web_components/src/icon/StopIcon.js";
import { SettingsIcon } from "@rosette/web_components/src/icon/SettingsIcon.js";
import type { ReactNode } from "react";
import React from "react";
import type { MusicTrack } from "@rosette/web_components/src/assets/MusicTrack.js";
import type { MusicAsset } from "@/components/assets/MusicAsset.js";
import type { AssetLoader } from "@/components/assets/AssetLoader.js";
import Link from "next/link";
import CloseIcon from "@rosette/web_components/src/icon/CloseIcon.js";
import { InlineIcon } from "@rosette/web_components/src/ui/InlineIcon.js";


const MUSIC_CONTROL_ICON = "inline h-[2.5em] group-hover:scale-110";


interface MusicControlsProps {
    assetLoader: AssetLoader;
    audioSettings: AudioSettings;
    activeAsset: MusicAsset;
    activeTrack: MusicTrack;
    isPlaying: boolean;
    closeControls: () => void;
    openSettings: () => void;
}


export function MusicControls({
    assetLoader, audioSettings, activeAsset, activeTrack, isPlaying,
    closeControls, openSettings,
}: MusicControlsProps) {

    const linkElems: ReactNode[] = [];
    for (const link of activeTrack.links) {
        linkElems.push(
            <Link
                className="my-3"
                key={link.linkText}
                href={link.url}
                target="_blank"
                onClick={() => activeAsset.pause()}>

                {link.linkText}
            </Link>,
        );
    }

    return (
        <>
            <div className="flex flex-row items-start gap-3 w-full">
                <div className="grow">
                    <p className="m-0 text-strong">
                        {activeTrack.title}
                    </p>
                    <p className="m-0 text-body">
                        {` by ${activeTrack.author}`}
                    </p>
                </div>
                <button
                    className="ml-auto text-subtle cursor-pointer"
                    onClick={closeControls}>

                    <InlineIcon>
                        <CloseIcon />
                    </InlineIcon>
                </button>
            </div>

            {linkElems}

            <div className="flex flex-row justify-center items-center gap-3 w-full pt-3">
                <button
                    className="group cursor-pointer"
                    title="Play Music"
                    onClick={() => activeAsset.tryPlay()}>

                    <PlayIcon className={cn(
                        MUSIC_CONTROL_ICON,
                        isPlaying ? "fill-text-subtle" : "fill-text-strong",
                    )} />
                </button>

                <button
                    className="group cursor-pointer"
                    title="Pause Music"
                    onClick={() => activeAsset.pause()}>

                    <PauseIcon className={cn(
                        MUSIC_CONTROL_ICON,
                        isPlaying ? "fill-text-strong" : "fill-text-subtle",
                    )} />
                </button>

                <button
                    className="group cursor-pointer"
                    title="Disable Music"
                    onClick={() => {
                        closeControls();
                        assetLoader.getAudioSettings().set(new AudioSettings(
                            audioSettings.soundVolume,
                            audioSettings.musicVolume,
                            true,
                        ));
                    }}>

                    <StopIcon className={cn(MUSIC_CONTROL_ICON, "fill-text-strong")} />
                </button>

                <button
                    className="group cursor-pointer"
                    title="Open Preferences"
                    onClick={() => {
                        closeControls();
                        openSettings();
                    }}>
                    <SettingsIcon className={cn(MUSIC_CONTROL_ICON, "p-[0.25em] fill-text-strong")} />
                </button>
            </div>
        </>
    );
}
